import React, { useCallback } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'

import { useAccount } from '../../../common/useAccount'

import { useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { ZapierPromoBanner } from '../../../components/BusinessEvent/ZapierPromoBanner/ZapierPromoBanner'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'

export const BusinessEventSettings = () => {
  const accountId = useAppSelector(selectAccountId)

  const { hasQuota } = useZocksQuota()

  const businessEventsEnabled = hasQuota('ai.businessEvent.value')

  const { account } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountUpdateLoading }] = useUpdateAccountMutation()

  const handleChange = useCallback(async () => {
    if (!account) {
      return
    }
    await updateAccount({ accountId, payload: { config: { ...account.config, businessEvent: !account.effectiveConfig.businessEvent } } })
  }, [account, accountId, updateAccount])

  if (!account) {
    return null
  }

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Client Signals</h4>
      {!businessEventsEnabled ? (
        <Upsell feature={DELTA_TRIAL_FEATURES['businessEvents']} />
      ) : (
        <>
          <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
            <i className='fs-5 bi bi-info-square zocks-text-primary' />
            <p className='d-flex flex-column paragraph-medium m-0'>
              <span className='zocks-text-gray-900'>
                Your AI Assistant detects important signals from meetings in your account - such as life events and referrals -, highlights them in meeting
                notes and sends them to Zapier to trigger your workflows.
              </span>
              <a
                className='zocks-text-primary text-decoration-underline'
                href='https://help.zocks.io/en/articles/13421694-zocks-client-signals'
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn more
              </a>
            </p>
          </div>
          <div className='d-flex gap-3'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.REPLACE}
              replaceElement={
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement='top'
                  overlay={<BootstrapTooltip className='zocks-text-gray-500'>Your account admin can edit this setting.</BootstrapTooltip>}
                >
                  <Form.Switch disabled={true} checked={account.effectiveConfig.businessEvent} />
                </OverlayTrigger>
              }
            >
              <Form.Switch onChange={handleChange} disabled={isAccountUpdateLoading} checked={account.effectiveConfig.businessEvent} />
            </ScopedBlock>
            <div className='d-flex flex-column gap-1'>
              <span className='paragraph-medium'>Enable detecting Client Signals</span>
              <span className='paragraph-small zocks-text-gray-700'>This affects all users in your account.</span>
            </div>
          </div>
          <ZapierPromoBanner orientation='horizontal' variant='ghost' />
        </>
      )}
    </div>
  )
}
